import { getConfig } from '../config'

// Declare gtag function for TypeScript
declare global {
  interface Window {
    gtag: (..._args: unknown[]) => void
    dataLayer: unknown[]
  }
}

// Get GA4 Measurement ID from config
const getGAMeasurementId = (): string | null => {
  const config = getConfig()
  return config.GA4_MEASUREMENT_ID || null
}

// Wrapper for gtag function
export const gtag = (..._args: unknown[]) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag(..._args)
  }
}

// Load gtag script dynamically
const loadGtagScript = (measurementId: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    // Check if script is already loaded
    if (document.querySelector(`script[src*="gtag/js?id=${measurementId}"]`)) {
      resolve()
      return
    }

    const script = document.createElement('script')
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Failed to load gtag script'))
    document.head.appendChild(script)
  })
}

// Initialize Google Analytics
export const initializeGA = async () => {
  const measurementId = getGAMeasurementId()

  if (!measurementId) {
    console.log('Google Analytics not initialized: No measurement ID provided')
    return
  }

  try {
    // Load gtag script dynamically
    await loadGtagScript(measurementId)

    // Initialize gtag
    window.dataLayer = window.dataLayer || []
    gtag('js', new Date())
    gtag('config', measurementId, {
      // Enhanced measurement for engagement tracking
      send_page_view: false // We'll handle page views manually
      // These are enabled by default in GA4 for engagement tracking:
      // - scroll tracking
      // - outbound clicks
      // - site search
      // - video engagement
      // - file downloads
    })

    console.log('Google Analytics initialized with ID:', measurementId)
  } catch (error) {
    console.error('Failed to initialize Google Analytics:', error)
  }
}

// Track page views
export const trackPageView = (url: string, title?: string) => {
  const measurementId = getGAMeasurementId()

  if (!measurementId) return

  gtag('config', measurementId, {
    page_path: url,
    page_title: title || document.title,
    page_location: window.location.href
  })
}

// Track custom events (optional for future use)
export const trackEvent = (action: string, parameters?: Record<string, unknown>) => {
  const measurementId = getGAMeasurementId()

  if (!measurementId) return

  gtag('event', action, {
    ...parameters,
    // Add custom parameters that help with engagement tracking
    engagement_time_msec: Date.now(),
    page_location: window.location.href,
    page_title: document.title
  })
}

// Track user login (useful for engagement)
export const trackLogin = (method: string = 'keycloak') => {
  trackEvent('login', {
    method
  })
}


// Track date selection (custom event for SmartEDA)
export const trackDateSelection = (selectedDate: string) => {
  trackEvent('select_date', {
    selected_date: selectedDate
  })
}

